import { useMemo } from 'react'
import { useQuery, useSuspenseQuery } from '@apollo/client'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { goodie as goodieHelpers, dateTime } from '@guestgoodie/helpers'
import clsx from 'clsx'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Table from '@/components/ui/Table'
import { Suspense } from '@/components/ui/Suspense'
import { amount, startDate, unitPrice, useStateToLabel } from '../utils/helpers'

function OrderRow({ order }: { order: GraphQL.OrderFragmentFragment }) {
  const stateToLabel = useStateToLabel()

  const { data } = useQuery(AdminAPI.goodie.GET, {
    variables: { id: order.goodieId }
  })

  const goodie = useMemo(
    () => getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, data?.getGoodie),
    [data]
  )

  const start = startDate(order)
  const state = stateToLabel[order.state]

  return (
    <Table.Row>
      <Table.Cell className={clsx('text-heading3', !goodie && 'animate-pulse')}>
        {goodie?.title}
      </Table.Cell>
      <Table.Cell>
        {dateTime.fullFormatter.format(new Date(order.insertedAt))}
      </Table.Cell>
      <Table.Cell>
        {start && dateTime.fullFormatter.format(new Date(start))}
      </Table.Cell>
      <Table.Cell>{state}</Table.Cell>
      <Table.Cell>{amount(order)}</Table.Cell>
      <Table.Cell
        className={clsx(
          'text-bold',
          order.state !== GraphQL.OrderState.Approved && 'line-through'
        )}
      >
        {goodieHelpers.toPrice({ price: unitPrice(order) })}
      </Table.Cell>
      <Table.Cell
        className={clsx(
          'text-heading3',
          order.state !== GraphQL.OrderState.Approved && 'line-through'
        )}
      >
        {goodieHelpers.toPrice({ price: order.price })}
      </Table.Cell>
    </Table.Row>
  )
}

interface Props {
  stayId: number
}

function StayOrders({ stayId }: Props) {
  const { t } = useLingui()

  const { data } = useSuspenseQuery(AdminAPI.order.GET_BY_STAY, {
    variables: { stayId }
  })

  const orders = useMemo(() => {
    return data.ordersByStay.map(function (order) {
      return getFragment(SharedAPI.order.ORDER_FRAGMENT, order)
    })
  }, [data])

  const total = useMemo(() => {
    return orders
      .filter(order => order.state === GraphQL.OrderState.Approved)
      .reduce((sum, order) => sum + order.price, 0)
  }, [orders])

  return (
    <Table.Root>
      <Table.Head>
        <Table.Row>
          <Table.Heading>
            <Trans>Name</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Ordered at</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Started at</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Status</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Amount</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Unit Price</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Total Price</Trans>
          </Table.Heading>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {orders.map(function (order) {
          return <OrderRow key={order.id} order={order} />
        })}
        {orders.length === 0 && <Table.Empty text={t`No orders found`} />}
      </Table.Body>
      <Table.Foot>
        <Table.Row>
          <Table.Cell colSpan={6} className="text-heading2">
            <Trans>Total</Trans>
          </Table.Cell>
          <Table.Cell className="text-heading2">
            {goodieHelpers.toPrice({
              price: total,
              zeroText: goodieHelpers.formatAsPrice(0)
            })}
          </Table.Cell>
        </Table.Row>
      </Table.Foot>
    </Table.Root>
  )
}

export default function SuspenseStayOrders(props: Props) {
  return (
    <Suspense>
      <StayOrders {...props} />
    </Suspense>
  )
}
